/**
 * whisper-gpu-policy — decides when a device should stop trying WebGPU.
 *
 * Why this exists (measured, 2026-07-23):
 * WebGPU runs Whisper 3-5x faster than the WASM fallback. Telemetry showed
 * WebGPU used in only ~48% of runs, and arithmetic on the fallback counters
 * pointed at our own persistence rule: the 2026-07-02 fix wrote
 * `localStorage.ac_whisper_wasm = '1'` on ANY webgpu_fallback, permanently and
 * with no re-probe. One bad run condemned that browser to the slow path for
 * every future visit.
 *
 * That is wrong for the dominant case, and our own worker comment says why:
 * WebGPU fp16 Whisper "intermittently emits degenerate output ... ~35% of
 * WebGPU runs ... NON-DETERMINISTIC (same device gives good + bad runs)".
 * A coin-flip event must never produce a permanent verdict. The split in
 * production was decisive: of 78 poisonings, 72 were `degenerate` (the
 * non-deterministic class) and only 6 were `inference_error` (a real
 * capability failure) — 92% were persisted for the wrong reason.
 *
 * Policy:
 *   - degenerate output      -> recover THIS run on WASM, persist NOTHING.
 *                               The next run tries the fast path again.
 *   - hard capability error  -> (OOM / device lost / "no available backend")
 *                               count a strike; only after `strikes` strikes
 *                               does the device skip WebGPU, and even then the
 *                               verdict expires so hardware/driver updates and
 *                               transient memory pressure heal themselves.
 *
 * `shouldPersist(reason)` / `isForced(state, now)` / `recordFailure(state, reason, now)`
 * are pure so they can be unit-tested without a browser.
 *
 * Exposed as `window.__whisperGpuPolicy` (browser) + module.exports (node tests).
 */
(function () {
    'use strict';

    var DEFAULTS = {
        strikes: 2,                       // consecutive hard failures before skipping WebGPU
        ttlMs: 7 * 24 * 60 * 60 * 1000,   // verdict expires -> re-probe the fast path
    };

    /* A hard capability failure: the GPU could not run the model at all.
       Everything else (degenerate/garbled output) is the coin-flip class. */
    function shouldPersist(reason) {
        var r = String(reason == null ? '' : reason).toLowerCase();
        if (!r) return false;
        return /inference_error|out of memory|oom|device.?lost|no available backend|adapter/.test(r);
    }

    function isForced(state, now, opts) {
        var O = merge(opts);
        if (!state || typeof state !== 'object') return false;
        var ts = +state.ts || 0;
        if (!ts || (now - ts) > O.ttlMs) return false;   // expired -> try WebGPU again
        return (+state.strikes || 0) >= O.strikes;
    }

    /* Returns the NEXT persisted state, or null when nothing should be stored.
       A non-persisting reason also leaves any existing state untouched. */
    function recordFailure(state, reason, now, opts) {
        if (!shouldPersist(reason)) return state || null;
        var O = merge(opts);
        var prev = (state && +state.ts && (now - +state.ts) <= O.ttlMs) ? (+state.strikes || 0) : 0;
        return { strikes: prev + 1, ts: now, reason: String(reason).slice(0, 60) };
    }

    /* A successful WebGPU run clears the record — the device just proved it works. */
    function recordSuccess() { return null; }

    function merge(opts) {
        var O = {};
        for (var k in DEFAULTS) O[k] = (opts && opts[k] != null) ? opts[k] : DEFAULTS[k];
        return O;
    }

    /* Storage-bound wrapper so each caller is two lines instead of its own
       copy of the JSON/try-catch dance. `legacyKey` (optional) is the v1 flag:
       it is REMOVED on first read rather than imported — it carries neither a
       reason nor a timestamp, and by the measured split was most likely set by
       a coin-flip run, so honouring it would keep the wrong verdict alive.
       `onEvent(name, payload)` is optional telemetry. */
    function bind(cfg) {
        cfg = cfg || {};
        var key = cfg.key, legacyKey = cfg.legacyKey;
        var emit = typeof cfg.onEvent === 'function' ? cfg.onEvent : function () {};
        function read() {
            try {
                if (legacyKey && localStorage.getItem(legacyKey) != null) {
                    localStorage.removeItem(legacyKey);
                    emit('gpu_verdict_migrated', { from: 'v1' });
                }
                var raw = localStorage.getItem(key);
                return raw ? JSON.parse(raw) : null;
            } catch (_) { return null; }
        }
        function write(next) {
            try {
                if (next) localStorage.setItem(key, JSON.stringify(next));
                else localStorage.removeItem(key);
            } catch (_) {}
        }
        return {
            isForced: function (now) { return isForced(read(), now || Date.now(), cfg); },
            noteFailure: function (reason, now) {
                var r = String(reason || 'unknown').slice(0, 60);
                write(recordFailure(read(), r, now || Date.now(), cfg));
                return shouldPersist(r);
            },
            noteSuccess: function () { if (read()) write(null); },
        };
    }

    var api = {
        shouldPersist: shouldPersist,
        isForced: isForced,
        recordFailure: recordFailure,
        recordSuccess: recordSuccess,
        bind: bind,
        DEFAULTS: DEFAULTS,
    };
    if (typeof window !== 'undefined') window.__whisperGpuPolicy = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
